import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Avatar,
  AvatarGroup,
  Tooltip,
  Chip,
  Typography,
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import {
  FiberManualRecord as OnlineIcon,
  Edit as EditingIcon,
  Visibility as ViewingIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import websocketService from '../services/websocket.service';
import EventBus from 'eventing-bus';

const CollaborativePresence = ({ workflowId, onUserSelect }) => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [userCursors, setUserCursors] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const currentUser = useSelector(state => state.Settings?.user);
  
  useEffect(() => {
    if (!workflowId || !websocketService.isConnected()) return;
    
    // Join workflow collaboration room
    websocketService.emit('workflow:collaboration:join', { 
      workflowId,
      user: {
        id: currentUser?._id,
        name: currentUser?.name,
        email: currentUser?.email,
        avatar: currentUser?.avatar
      }
    });
    
    // Listen for user presence updates
    const handleUserJoined = (data) => {
      if (data.workflowId === workflowId && data.user.id !== currentUser?._id) {
        setActiveUsers(prev => {
          const exists = prev.find(u => u.id === data.user.id);
          if (!exists) {
            EventBus.publish('info', `${data.user.name} joined the workflow`);
            return [...prev, { ...data.user, status: 'viewing', joinedAt: new Date() }];
          }
          return prev;
        });
      }
    };
    
    const handleUserLeft = (data) => {
      if (data.workflowId === workflowId) {
        setActiveUsers(prev => {
          const user = prev.find(u => u.id === data.userId);
          if (user) {
            EventBus.publish('info', `${user.name} left the workflow`);
          }
          return prev.filter(u => u.id !== data.userId);
        });
        setUserCursors(prev => {
          const updated = { ...prev };
          delete updated[data.userId];
          return updated;
        });
      }
    };
    
    const handleUserActivity = (data) => {
      if (data.workflowId === workflowId && data.userId !== currentUser?._id) {
        setActiveUsers(prev => prev.map(u => 
          u.id === data.userId 
            ? { ...u, status: data.activity, lastActivity: new Date() }
            : u
        ));
      }
    };
    
    const handleCursorMove = (data) => {
      if (data.workflowId === workflowId && data.userId !== currentUser?._id) {
        setUserCursors(prev => ({
          ...prev,
          [data.userId]: {
            x: data.position.x,
            y: data.position.y,
            nodeId: data.nodeId,
            timestamp: new Date()
          }
        }));
      }
    };
    
    const handleActiveUsers = (data) => {
      if (data.workflowId === workflowId) {
        setActiveUsers(data.users.filter(u => u.id !== currentUser?._id));
      }
    };
    
    // Subscribe to events
    const unsubscribeUserJoined = EventBus.on('workflow:user:joined', handleUserJoined);
    const unsubscribeUserLeft = EventBus.on('workflow:user:left', handleUserLeft);
    const unsubscribeUserActivity = EventBus.on('workflow:user:activity', handleUserActivity);
    const unsubscribeCursorMove = EventBus.on('workflow:cursor:move', handleCursorMove);
    const unsubscribeActiveUsers = EventBus.on('workflow:active:users', handleActiveUsers);
    
    // Request current active users
    websocketService.emit('workflow:collaboration:getUsers', { workflowId });
    
    return () => {
      // Leave collaboration room
      websocketService.emit('workflow:collaboration:leave', { workflowId });
      
      // Unsubscribe from events
      unsubscribeUserJoined();
      unsubscribeUserLeft();
      unsubscribeUserActivity();
      unsubscribeCursorMove();
      unsubscribeActiveUsers();
    };
  }, [workflowId, currentUser]);
  
  // Emit cursor position
  const emitCursorPosition = (position, nodeId) => {
    if (websocketService.isConnected()) {
      websocketService.emit('workflow:cursor:move', {
        workflowId,
        position,
        nodeId
      });
    }
  };
  
  // Emit user activity
  const emitActivity = (activity) => {
    if (websocketService.isConnected()) {
      websocketService.emit('workflow:user:activity', {
        workflowId,
        activity
      });
    }
  };
  
  const handleShowUsers = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseUsers = () => {
    setAnchorEl(null);
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'editing':
        return <EditingIcon fontSize="small" />;
      case 'viewing':
        return <ViewingIcon fontSize="small" />;
      default:
        return <OnlineIcon fontSize="small" />;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'editing':
        return 'primary';
      case 'viewing':
        return 'default';
      default:
        return 'success';
    }
  };
  
  const getUserInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??';
  };
  
  const getUserColor = (userId) => {
    const colors = [
      '#f44336', '#e91e63', '#9c27b0', '#673ab7',
      '#3f51b5', '#2196f3', '#00bcd4', '#009688',
      '#4caf50', '#8bc34a', '#ff9800', '#ff5722'
    ];
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  };
  
  const open = Boolean(anchorEl);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {/* Active Users Avatars */}
      <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: 14 } }}>
        {activeUsers.map(user => (
          <Tooltip key={user.id} title={`${user.name} - ${user.status}`}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={getStatusIcon(user.status)}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: 'background.paper',
                  color: `${getStatusColor(user.status)}.main`,
                  boxShadow: '0 0 0 2px #fff',
                  '&::after': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    animation: user.status === 'editing' ? 'ripple 1.2s infinite ease-in-out' : 'none',
                    border: '1px solid currentColor',
                    content: '""',
                  },
                },
                '@keyframes ripple': {
                  '0%': {
                    transform: 'scale(.8)',
                    opacity: 1,
                  },
                  '100%': {
                    transform: 'scale(2.4)',
                    opacity: 0,
                  },
                },
              }}
            >
              <Avatar
                sx={{ 
                  bgcolor: getUserColor(user.id),
                  cursor: 'pointer'
                }}
                src={user.avatar}
                onClick={() => onUserSelect?.(user)}
              >
                {!user.avatar && getUserInitials(user.name)}
              </Avatar>
            </Badge>
          </Tooltip>
        ))}
      </AvatarGroup>
      
      {/* Show All Users Button */}
      {activeUsers.length > 0 && (
        <IconButton size="small" onClick={handleShowUsers}>
          <Badge badgeContent={activeUsers.length} color="primary">
            <PeopleIcon />
          </Badge>
        </IconButton>
      )}
      
      {/* Users Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseUsers}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            Active Collaborators ({activeUsers.length})
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {activeUsers.map(user => (
              <ListItem key={user.id}>
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <OnlineIcon 
                        sx={{ 
                          fontSize: 12, 
                          color: 'success.main',
                          backgroundColor: 'background.paper',
                          borderRadius: '50%'
                        }} 
                      />
                    }
                  >
                    <Avatar
                      sx={{ bgcolor: getUserColor(user.id) }}
                      src={user.avatar}
                    >
                      {!user.avatar && getUserInitials(user.name)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={user.status}
                        size="small"
                        icon={getStatusIcon(user.status)}
                        color={getStatusColor(user.status)}
                      />
                      <Typography variant="caption" color="textSecondary">
                        Joined {new Date(user.joinedAt).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
      
      {/* Render User Cursors */}
      {Object.entries(userCursors).map(([userId, cursor]) => {
        const user = activeUsers.find(u => u.id === userId);
        if (!user) return null;
        
        return (
          <Box
            key={userId}
            sx={{
              position: 'fixed',
              left: cursor.x,
              top: cursor.y,
              pointerEvents: 'none',
              zIndex: 9999,
              transition: 'all 0.1s ease-out'
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: getUserColor(userId),
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2
              }}
            >
              <Typography sx={{ fontSize: 10, color: 'white', fontWeight: 'bold' }}>
                {getUserInitials(user.name).charAt(0)}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: 22,
                left: -10,
                bgcolor: 'background.paper',
                px: 0.5,
                borderRadius: 0.5,
                boxShadow: 1,
                whiteSpace: 'nowrap'
              }}
            >
              {user.name}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default CollaborativePresence;

// Export utility functions for use in other components
export const useCollaboration = (workflowId) => {
  const emitActivity = (activity) => {
    if (websocketService.isConnected()) {
      websocketService.emit('workflow:user:activity', {
        workflowId,
        activity
      });
    }
  };
  
  const emitNodeLock = (nodeId, locked) => {
    if (websocketService.isConnected()) {
      websocketService.emit('workflow:node:lock', {
        workflowId,
        nodeId,
        locked
      });
    }
  };
  
  const emitChange = (changeType, data) => {
    if (websocketService.isConnected()) {
      websocketService.emit('workflow:change', {
        workflowId,
        changeType,
        data,
        timestamp: new Date()
      });
    }
  };
  
  return {
    emitActivity,
    emitNodeLock,
    emitChange
  };
};